import { useState, useEffect, useMemo, useCallback } from 'react';
import Card from 'components/card/Card';
import {
  Button,
  Flex,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  PaginationState,
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { Invite } from 'services/@types';
import InviteService from 'services/invite.api';
import BusinessService from 'services/business.api';
import useColumns from './hooks/useColumns';
import { buildInviteParams } from './buildInviteParams';
import { resolveResendMethod } from './resolveResendMethod';

export interface InviteRow extends Invite {
  businessName?: string;
}

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_INDEX = 1;

const STATUS_FILTERS = ['all', 'pending', 'accepted', 'rejected', 'cancelled'];
const TYPE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'seat', label: 'Seat' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'client', label: 'Client' },
  { value: 'quote', label: 'Quote' },
];

export default function InvitesOverview() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const toast = useToast();

  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const pages = useMemo(() => {
    const arr = [];
    for (let i = 1; i <= totalPages; i++) arr.push(i);
    return arr;
  }, [totalPages]);

  const fetchInvites = useCallback(async () => {
    try {
      const params = buildInviteParams({
        pageSize,
        pageIndex,
        statusFilter,
        typeFilter,
      });
      const data = await InviteService.getInvites(params);
      if (data?.results) {
        const rawInvites = data.results as Invite[];

        // Resolve business names
        const uniqueBusinessIDs = [
          ...new Set(
            rawInvites
              .map((inv) => inv.businessID)
              .filter((id): id is string => !!id),
          ),
        ];

        let businessMap: Record<string, string> = {};
        if (uniqueBusinessIDs.length > 0) {
          try {
            const businessData = await BusinessService.getBusinesses({
              limit: 200,
            });
            if (businessData?.results) {
              businessMap = businessData.results.reduce(
                (acc, biz) => {
                  if (biz.id) acc[biz.id] = biz.businessName;
                  return acc;
                },
                {} as Record<string, string>,
              );
            }
          } catch (err) {
            console.error('Failed to fetch businesses:', err);
          }
        }

        const inviteRows: InviteRow[] = rawInvites.map((inv) => ({
          ...inv,
          businessName: inv.businessID
            ? businessMap[inv.businessID] || undefined
            : undefined,
        }));

        setInvites(inviteRows);
        setTotalPages(data.totalPages);
      }
    } catch (error) {
      console.error(error);
    }
  }, [pageSize, pageIndex, statusFilter, typeFilter]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const handleResend = useCallback(
    async (id: string, inviteType?: string) => {
      try {
        const method = resolveResendMethod(inviteType);
        if (method === 'quote') {
          await InviteService.resendInvite(id);
        } else {
          await InviteService.updateInvite(id, { status: 'pending' } as any);
        }
        toast({
          title: 'Invite resent',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchInvites();
      } catch (error) {
        console.error(error);
        toast({
          title: 'Failed to resend invite',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [fetchInvites, toast],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await InviteService.deleteInvite(id);
        toast({
          title: 'Invite deleted',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchInvites();
      } catch (error) {
        console.error(error);
        toast({
          title: 'Failed to delete invite',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [fetchInvites, toast],
  );

  const columns = useColumns({
    onResend: handleResend,
    onDelete: handleDelete,
  });

  const table = useReactTable({
    data: invites,
    columns,
    state: { columnFilters },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  return (
    <Flex direction="column" pt={{ sm: '125px', lg: '75px' }}>
      <Card px="0px">
        <Flex
          direction="column"
          w="100%"
          overflowX={{ sm: 'scroll', lg: 'hidden' }}
        >
          <Flex px="25px" mb="8px" justify="space-between" align="center">
            <Text color={textColor} fontSize="22px" fontWeight="700">
              Invites
            </Text>
            <Flex gap="8px" align="center" wrap="wrap">
              <Text fontSize="sm" color="gray.500">
                Type:
              </Text>
              <Select
                size="sm"
                w="140px"
                borderRadius="8px"
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPagination((prev) => ({ ...prev, pageIndex: 1 }));
                }}
              >
                {TYPE_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </Select>
              <Text fontSize="sm" color="gray.500">
                Status:
              </Text>
              <Select
                size="sm"
                w="140px"
                borderRadius="8px"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination((prev) => ({ ...prev, pageIndex: 1 }));
                }}
              >
                {STATUS_FILTERS.map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </Select>
            </Flex>
          </Flex>
          <Table variant="simple" color="gray.500" mb="24px">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id} borderColor={borderColor}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <Tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id} borderColor={borderColor}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
          <Flex px="25px" pb="20px" justify="space-between" align="center">
            <Flex gap="8px" align="center">
              <Select
                size="sm"
                w="80px"
                borderRadius="8px"
                value={pageSize}
                onChange={(e) =>
                  setPagination((prev) => ({
                    ...prev,
                    pageSize: Number(e.target.value),
                  }))
                }
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Select>
              <Text fontSize="sm" color="gray.500">
                per page
              </Text>
            </Flex>
            <Flex gap="4px">
              {pages.map((page) => (
                <Button
                  key={page}
                  size="sm"
                  variant={page === pageIndex ? 'brand' : 'ghost'}
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, pageIndex: page }))
                  }
                >
                  {page}
                </Button>
              ))}
            </Flex>
          </Flex>
        </Flex>
      </Card>
    </Flex>
  );
}
